import { useMemo, useState } from "react";
import { type AnalyteGroupRow, type AnalyteUpdateResult, type Branch, type BranchFormulaAnalyte, checkStudyFormulasByBranch } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { type AnalyteDraft, AnalyteFields, analyteDraft, draftsPayload, emptyFormulaProblem, formulaStudy, type IntervalDraft, validateDrafts } from "./CatalogueEditors.js";
import { DeleteAnalytePanel } from "./AnalyteRemoval.js";
import { Alert, Button, Chip, Empty, Loading } from "./ui.js";

/** What makes two rows of one clave "the same analyte": everything the editor edits. */
export function definitionKey(a: AnalyteGroupRow["analyte"]): string {
  const iv = a.reference_intervals
    .slice()
    .sort((x, y) => x.sort_order - y.sort_order)
    .map((r) => [r.branch_id ?? null, r.sex, r.age_min_days, r.age_max_days, r.condition, r.low, r.high, r.expected_text, r.display_text]);
  const bf = (a.branch_formulas ?? []).map((b) => [b.branch_id, b.formula]).sort();
  return JSON.stringify([a.name, a.kind, a.unit, a.decimals, a.options ?? null, a.formula, iv, bf]);
}

/** The study a row belongs to as the formula checker sees it, with the draft in place of its analyte (per-branch formulas included, ADR 0032). */
function studyWith(row: AnalyteGroupRow, draft: AnalyteDraft): BranchFormulaAnalyte[] {
  const self = formulaStudy([draft])[0]!;
  return [...row.siblings.map((s) => ({ id: s.id, code: s.code, kind: s.kind, decimals: s.decimals, formula: s.formula, branch_formulas: s.branch_formulas ?? [] })), self];
}

const branchName = (branches: Branch[], id: string | null) => (id === null ? null : (branches.find((b) => b.id === id)?.name ?? "otra sucursal"));

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Catálogo → Analitos → one clave (pilot request of 2026-11-03, "solo modificar los analitos, y
 * no buscarlos por estudio"). The analyte is a row of each study that carries the clave, so the
 * editor starts from one study's row and applies the edit to the studies ticked below — never
 * silently to all: the list says which studies hold the same definition and which differ, and
 * the save first shows "Se actualizará en N estudios: …". Each study gets a new version (ADR 0009).
 */
export function AnalyteGroupEditor({ code, branches, onSaved, onCancel }: { code: string; branches: Branch[]; onSaved: (r: AnalyteUpdateResult) => void; onCancel: () => void }) {
  const group = useQuery(() => get("catalogueAnalyteGroup", { query: { code } }), [code]);
  if (group.loading && !group.data) return <Loading />;
  if (!group.data) return <Empty icon="warning" title="No se encontró el analito" hint={group.error ?? undefined} />;
  return <GroupForm key={code} rows={group.data.rows} code={code} branches={branches} onSaved={onSaved} onCancel={onCancel} />;
}

function GroupForm({ rows, code, branches, onSaved, onCancel }: { rows: AnalyteGroupRow[]; code: string; branches: Branch[]; onSaved: (r: AnalyteUpdateResult) => void; onCancel: () => void }) {
  const toast = useToast();
  // Start from the definition most studies share, so a common edit begins from the common case.
  const initial = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(definitionKey(r.analyte), (counts.get(definitionKey(r.analyte)) ?? 0) + 1);
    let best = 0;
    rows.forEach((r, i) => {
      if ((counts.get(definitionKey(r.analyte)) ?? 0) > (counts.get(definitionKey(rows[best]!.analyte)) ?? 0)) best = i;
    });
    return best;
  }, [rows]);
  const [sourceIdx, setSourceIdx] = useState(initial);
  const source = rows[sourceIdx]!;
  const [draft, setDraft] = useState<AnalyteDraft>(() => analyteDraft(rows[initial]!.analyte));
  const [chosen, setChosen] = useState<Set<string>>(() => new Set([rows[initial]!.study.id]));
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  const sourceKey = definitionKey(source.analyte);
  const targets = rows.filter((r) => chosen.has(r.study.id) || r.study.id === source.study.id);
  // The formula is checked below against each study's own analytes, not against this lone draft.
  const problems = useMemo(() => new Map([...validateDrafts([draft])].filter(([k]) => !k.endsWith(":formula"))), [draft]);
  const fStudy = useMemo(() => studyWith(source, draft), [source, draft]);
  // ADR 0023 in each ticked study: a formula may only read that study's own analytes.
  // Every effective set of each study is checked: the general one and each branch's (ADR 0032).
  const formulaIssues = useMemo(() => {
    if (draft.kind !== "numeric") return [];
    const empty = emptyFormulaProblem(draft);
    if (empty) return [{ clave: source.study.clave, message: empty.message }];
    return targets.flatMap((r) => {
      const p = checkStudyFormulasByBranch(studyWith(r, draft)).get(draft.key);
      const where = branchName(branches, p?.branch_id ?? null);
      return p ? [{ clave: r.study.clave, message: where ? `en ${where}: ${p.message}` : p.message }] : [];
    });
  }, [draft, targets, source, branches]);
  const sourceFormulaProblem = useMemo(() => {
    if (draft.kind !== "numeric") return null;
    return emptyFormulaProblem(draft) ?? checkStudyFormulasByBranch(fStudy).get(draft.key) ?? null;
  }, [draft, fStudy]);
  const blocking = [...problems.values()][0] ?? formulaIssues.find((f) => f.clave !== source.study.clave)?.message ?? null;
  const openItems = targets.reduce((n, r) => n + r.open_items, 0);

  const patch = (p: Partial<AnalyteDraft>) => setDraft((d) => ({ ...d, ...p }));
  const patchInterval = (rk: string, p: Partial<IntervalDraft>) =>
    setDraft((d) => ({
      ...d,
      intervals: d.intervals.map((r) => {
        if (r.key !== rk) return r;
        const next = { ...r, ...p };
        if (!next.displayTouched && ("low" in p || "high" in p || "expected" in p)) next.display = "";
        return next;
      }),
    }));
  const toggle = (id: string) =>
    setChosen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const all = rows.every((r) => chosen.has(r.study.id) || r.study.id === source.study.id);

  function startFrom(i: number) {
    setSourceIdx(i);
    setDraft(analyteDraft(rows[i]!.analyte));
    setChosen(new Set([rows[i]!.study.id]));
    setConfirming(false);
  }

  async function save() {
    const [p] = draftsPayload([draft]);
    setBusy(true);
    try {
      const res = await run<"catalogue.analyte.update", AnalyteUpdateResult>("catalogue.analyte.update", {
        analyte_id: source.analyte.id,
        also_study_ids: targets.filter((r) => r.study.id !== source.study.id).map((r) => r.study.id),
        name: p!.name,
        kind: p!.kind as "numeric" | "text" | "reference" | "document",
        unit: p!.unit,
        decimals: p!.decimals,
        options: p!.options,
        formula: p!.formula,
        branch_formulas: p!.branch_formulas,
        reference_intervals: p!.reference_intervals,
      });
      toast.success(`${res.code}: guardado en ${plural(res.studies.length, "estudio", "estudios")}. Las órdenes con resultados conservan la versión anterior.`);
      onSaved(res);
    } catch (e) {
      toast.error(errorMessage(e));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      {rows.length > 1 && new Set(rows.map((r) => definitionKey(r.analyte))).size > 1 && (
        <Alert tone="amber">
          Los estudios no tienen este analito igual. Estás editando el de <strong>{source.study.clave}</strong>; los que difieren se marcan abajo.
        </Alert>
      )}
      <section className="card stack" style={{ padding: 14, gap: 10 }} aria-label="Analito">
        <AnalyteFields
          analyte={draft}
          branches={branches}
          problems={problems}
          study={fStudy}
          formulaProblem={sourceFormulaProblem}
          codeLocked
          kinds={["numeric", "text", "reference", "document"]}
          onPatch={patch}
          onPatchInterval={patchInterval}
        />
        {problems.get(draft.key) && <Alert tone="red">{problems.get(draft.key)}</Alert>}
      </section>

      <section className="stack" style={{ gap: 8 }} aria-label="Estudios con esta clave">
        <div className="row between wrap" style={{ gap: 8 }}>
          <strong>
            {rows.length === 1 ? `Solo ${source.study.clave} tiene la clave ${code}` : `Estudios con la clave ${code} (${rows.length})`}
          </strong>
          {rows.length > 1 && (
            <label className="row small" style={{ gap: 8, minHeight: 44 }}>
              <input
                type="checkbox"
                checked={all}
                onChange={(e) => setChosen(new Set(e.target.checked ? rows.map((r) => r.study.id) : [source.study.id]))}
                style={{ width: 22, height: 22 }}
              />
              Aplicar a todos los estudios con esta clave
            </label>
          )}
        </div>
        {rows.map((r, i) => {
          const isSource = r.study.id === source.study.id;
          const same = definitionKey(r.analyte) === sourceKey;
          const issue = formulaIssues.find((f) => f.clave === r.study.clave && (chosen.has(r.study.id) || isSource));
          return (
            <div key={r.study.id} className="list-row" style={{ minHeight: 52, gap: 10, flexWrap: "wrap" }}>
              <input
                type="checkbox"
                aria-label={`Aplicar en ${r.study.clave}`}
                checked={isSource || chosen.has(r.study.id)}
                disabled={isSource}
                onChange={() => toggle(r.study.id)}
                style={{ width: 22, height: 22 }}
              />
              <span className="mono muted" style={{ width: 90 }}>
                {r.study.clave}
              </span>
              <span className="grow">
                {r.study.name}
                {!r.study.is_active && (
                  <>
                    {" "}
                    <Chip>Inactivo</Chip>
                  </>
                )}
              </span>
              {isSource ? <Chip tone="blue">Editando</Chip> : same ? <Chip tone="green">Igual</Chip> : <Chip tone="amber">Distinto</Chip>}
              {!isSource && !same && (
                <Button size="sm" variant="ghost" onClick={() => startFrom(i)}>
                  Editar desde este
                </Button>
              )}
              {issue && <span className="small" style={{ color: "var(--red)", flexBasis: "100%" }}>{`Fórmula en ${issue.clave}: ${issue.message}`}</span>}
            </div>
          );
        })}
      </section>

      {deleting ? (
        <DeleteAnalytePanel code={code} name={source.analyte.name} rows={rows} onDone={onSaved} onBack={() => setDeleting(false)} />
      ) : confirming ? (
        <div role="alertdialog" aria-label="Confirmar cambios" className="stack" style={{ gap: 10 }}>
          <Alert tone="blue">
            <strong>
              Se actualizará en {plural(targets.length, "estudio", "estudios")}: {targets.map((r) => `${r.study.clave} · ${r.study.name}`).join(", ")}.
            </strong>{" "}
            Cada uno queda en una versión nueva. Las órdenes que ya tienen resultados conservan la versión con la que se registraron
            {openItems > 0 ? `; ${plural(openItems, "orden que aún no tiene resultados toma", "órdenes que aún no tienen resultados toman")} el cambio.` : "."}
          </Alert>
          <div className="row end">
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Volver
            </Button>
            <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
              Confirmar y guardar
            </Button>
          </div>
        </div>
      ) : (
        <div className="row between wrap" style={{ gap: 8 }}>
          <Button variant="ghost" icon="trash" onClick={() => setDeleting(true)}>
            Eliminar del catálogo
          </Button>
          <span className="row" style={{ gap: 8 }}>
            <Button variant="ghost" onClick={onCancel}>
              Cancelar
            </Button>
            <Button variant="primary" icon="check" disabled={blocking !== null || sourceFormulaProblem !== null} onClick={() => setConfirming(true)}>
              Guardar
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}
